/**
 * Notification Utility
 * Simple function to send notifications from anywhere in the app
 * Supports both Chrome extension and browser notifications
 */
import chromeNotifications from './chrome-notifications';

/**
 * Send a notification
 * @param title Notification title
 * @param message Notification message
 * @param actionUrl Optional URL to open when clicked
 * @returns Promise with notification ID or null if not supported
 */
export const notify = (
  title: string,
  message: string,
  actionUrl?: string
): Promise<string | null> => {
  // Send Chrome notifications if in extension context, otherwise browser notifications
  return chromeNotifications.showSpotlightNotification(title, message, actionUrl);
};

/**
 * Send a notification with success styling
 */
export const notifySuccess = (
  message: string,
  title = '✅ Success',
  actionUrl?: string
): Promise<string | null> => {
  return notify(title, message, actionUrl);
};

/**
 * Send a notification with error styling
 */
export const notifyError = (
  message: string,
  title = '❌ Error',
  actionUrl?: string
): Promise<string | null> => {
  return notify(title, message, actionUrl);
};

/**
 * Send a notification with warning styling
 */
export const notifyWarning = (
  message: string,
  title = '⚠️ Warning',
  actionUrl?: string
): Promise<string | null> => {
  return notify(title, message, actionUrl);
};

/**
 * Send a notification with info styling
 */
export const notifyInfo = (
  message: string,
  title = 'ℹ️ Information',
  actionUrl?: string
): Promise<string | null> => {
  return notify(title, message, actionUrl);
};

/**
 * Request notification permission
 */
export const requestNotificationPermission = () => {
  return chromeNotifications.requestNotificationPermission();
};

/**
 * Check if notifications are supported
 */
export const isNotificationSupported = () => {
  return chromeNotifications.isExtension || chromeNotifications.isBrowserNotificationSupported;
};

/**
 * Get current notification permission status
 */
export const getNotificationPermission = (): NotificationPermission => {
  if (chromeNotifications.isExtension) {
    return 'granted'; // Chrome extensions have notifications by default
  }
  
  if (chromeNotifications.isBrowserNotificationSupported) {
    return Notification.permission;
  }
  
  return 'denied';
};

export default {
  notify,
  notifySuccess,
  notifyError,
  notifyWarning,
  notifyInfo,
  requestNotificationPermission,
  isNotificationSupported,
  getNotificationPermission
}; 